import './DataPage.css';

function normalizeStatus(status) {
  if (status === 'success' || status === 'new') {
    return 'success';
  }
  if (status === 'exists') {
    return 'exists';
  }
  if (status === 'pending' || status === 'verifying') {
    return status;
  }
  return 'failed';
}

function ImportStatusBadge({ row, successLabel, existsLabel, failedLabel }) {
  const status = normalizeStatus(row.status);

  if (status === 'verifying') {
    return (
      <span className="data-page__status data-page__status--verifying">
        verifying…
      </span>
    );
  }

  if (status === 'pending') {
    return (
      <span className="data-page__status data-page__status--pending">pending</span>
    );
  }

  if (status === 'success') {
    return (
      <span className="data-page__status data-page__status--active">{successLabel}</span>
    );
  }

  if (status === 'exists') {
    return (
      <span className="data-page__status data-page__status--exists" title={row.error}>
        {existsLabel}
      </span>
    );
  }

  return (
    <span className="data-page__status data-page__status--failed">
      {row.error || failedLabel}
    </span>
  );
}

function rowClassName(status) {
  if (status === 'failed') {
    return 'data-page__import-preview-row--invalid';
  }
  if (status === 'exists') {
    return 'data-page__import-preview-row--exists';
  }
  if (status === 'success') {
    return 'data-page__import-preview-row--success';
  }
  if (status === 'verifying') {
    return 'data-page__import-preview-row--verifying';
  }
  return 'data-page__import-preview-row--pending';
}

function ImportPreviewModal({
  title,
  fileName,
  columns,
  rows,
  total,
  newCount,
  existsCount,
  invalidCount,
  successCount,
  failedCount,
  pendingCount = 0,
  verifyingIndex = null,
  loading,
  confirming,
  onClose,
  onConfirm,
  loadingMessage = 'Checking your CSV...',
  successLabel = 'new',
  existsLabel = 'already exists',
  failedLabel = 'invalid',
  confirmLabel,
}) {
  const resolvedSuccessCount = successCount ?? newCount ?? 0;
  const resolvedFailedCount = failedCount ?? invalidCount ?? 0;
  const showTable = Array.isArray(rows) && rows.length > 0;
  const canConfirm = resolvedSuccessCount > 0 && !loading && !confirming;

  return (
    <div className="data-page__modal-backdrop" onClick={onClose}>
      <div
        className="data-page__modal data-page__modal--import-preview"
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-preview-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="import-preview-title">{title}</h3>
        {fileName && <p className="data-page__import-preview-file">{fileName}</p>}

        {loading && !showTable && (
          <p className="data-page__import-preview-loading">{loadingMessage}</p>
        )}

        {showTable && (
          <>
            <div className="data-page__import-preview-summary">
              <span>{total} total</span>
              <span className="data-page__import-preview-new">
                {resolvedSuccessCount} {successLabel}
              </span>
              {(existsCount ?? 0) > 0 && (
                <span className="data-page__import-preview-exists">
                  {existsCount} {existsLabel}
                </span>
              )}
              {resolvedFailedCount > 0 && (
                <span className="data-page__import-preview-invalid">
                  {resolvedFailedCount} {failedLabel}
                </span>
              )}
              {loading && (
                <span className="data-page__import-preview-verifying">
                  {verifyingIndex
                    ? `Checking row ${verifyingIndex}…`
                    : pendingCount > 0
                      ? `Checking… (${pendingCount} left)`
                      : 'Checking…'}
                </span>
              )}
            </div>

            <div className="data-page__import-preview-table-wrap">
              <table className="data-page__import-preview-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    {columns.map((column) => (
                      <th key={column.key}>{column.label}</th>
                    ))}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const status = normalizeStatus(row.status);
                    return (
                      <tr key={row.row} className={rowClassName(status)}>
                        <td>{row.row}</td>
                        {columns.map((column) => (
                          <td key={column.key}>
                            {column.render ? column.render(row) : row[column.key] ?? '—'}
                          </td>
                        ))}
                        <td>
                          <ImportStatusBadge
                            row={row}
                            successLabel={successLabel}
                            existsLabel={existsLabel}
                            failedLabel={failedLabel}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="data-page__modal-actions">
          <button
            type="button"
            className="data-page__modal-btn"
            onClick={onClose}
            disabled={confirming}
          >
            Cancel
          </button>
          <button
            type="button"
            className="data-page__modal-btn data-page__modal-btn--primary"
            onClick={onConfirm}
            disabled={!canConfirm}
          >
            {confirming
              ? 'Adding...'
              : confirmLabel || `Add ${resolvedSuccessCount} Item(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImportPreviewModal;
